import { useTranslation } from 'react-i18next'
import { getLegalDoc, parseLegalBody, type LegalDocId, type LegalLocale } from '@redevicer/shared'
import type { CSSProperties } from 'react'

function uiLocale(lng: string): LegalLocale {
  return lng?.toLowerCase().startsWith('lt') ? 'lt' : 'en'
}

function isRevisionParagraph(text: string): boolean {
  const t = text.trim()
  return /^Redakcija:/i.test(t) || /^Last updated:/i.test(t) || /^Paskutinis atnaujinimas:/i.test(t)
}

function splitLegalBlocks(blocks: ReturnType<typeof parseLegalBody>) {
  let i = 0
  let revision: string | null = null
  if (blocks[0]?.type === 'paragraph' && isRevisionParagraph(blocks[0].text)) {
    revision = blocks[0].text
    i = 1
  }
  const headerMeta: string[] = []
  while (i < blocks.length && blocks[i].type === 'paragraph') {
    headerMeta.push((blocks[i] as { type: 'paragraph'; text: string }).text)
    i += 1
  }
  return { revision, headerMeta, contentBlocks: blocks.slice(i) }
}

function IconLegalDoc() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M8 4h8l4 4v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M16 4v4h4M9 12h6M9 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

export interface LegalDocumentPageProps {
  docId: LegalDocId
  onBack: () => void
}

export default function LegalDocumentPage({ docId, onBack }: LegalDocumentPageProps) {
  const { i18n, t } = useTranslation()
  const doc = getLegalDoc(docId, uiLocale(i18n.language))
  const { revision, headerMeta, contentBlocks } = splitLegalBlocks(parseLegalBody(doc.body))

  return (
    <div className="vc-legal-page vc-legal-page--modern">
      <div className="vc-legal-page-toolbar">
        <button type="button" className="vc-legal-back vc-legal-back--pill" onClick={onBack}>
          ← {t('vc.legal.back')}
        </button>
      </div>
      <article className="vc-legal-article vc-legal-article--app">
        <div className="vc-legal-article__glow" aria-hidden="true" />
        <header className="vc-legal-header vc-legal-header--app">
          <div className="vc-legal-header__icon" aria-hidden="true">
            <IconLegalDoc />
          </div>
          <h1 className="vc-legal-title">{doc.title}</h1>
          {revision ? <p className="vc-legal-revision">{revision}</p> : null}
          {headerMeta.length > 0 ? (
            <p className="vc-legal-header__meta">{headerMeta.join(' ')}</p>
          ) : null}
        </header>
        <div className="vc-legal-body vc-legal-body--app">
          {contentBlocks.map((block, i) => {
            if (block.type === 'paragraph') {
              return (
                <p key={`p-${i}`} className="vc-legal-p">
                  {block.text}
                </p>
              )
            }
            return (
              <section
                key={`s-${block.number}-${i}`}
                className="vc-legal-section vc-legal-section--card"
                style={{ '--legal-i': i } as CSSProperties}
              >
                <h2 className="vc-legal-section__title">
                  <span className="vc-legal-section__num">{block.number}</span>
                  {block.title}
                </h2>
                {block.bullets.length > 0 && (
                  <ul className="vc-legal-list">
                    {block.bullets.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                )}
                {block.tail ? <p className="vc-legal-p">{block.tail}</p> : null}
              </section>
            )
          })}
        </div>
      </article>
    </div>
  )
}
